import { useQuery } from '@tanstack/react-query';
import { useProject } from '@/contexts/ProjectContext';
import { Clock } from 'lucide-react';
import { formatTimeAgo } from '@/lib/pattern-categories';
import { getGBrainPatterns } from '@/lib/api';

/**
 * RecentActivityPanel
 *
 * Shows last 5 patterns with timestamps
 */
export function RecentActivityPanel(): React.ReactElement | null {
  const { selectedProjectId } = useProject();

  const { data: patternsData } = useQuery({
    queryKey: ['g-brain-patterns', selectedProjectId],
    queryFn: () => {
      if (!selectedProjectId) throw new Error('No project selected');
      return getGBrainPatterns(selectedProjectId);
    },
    enabled: !!selectedProjectId,
    staleTime: 10_000,
    refetchInterval: 10_000,
  });

  const patterns = patternsData?.patterns ?? [];
  const recent = [...patterns].reverse().slice(0, 5);

  if (!selectedProjectId) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
        Recent Activity
      </div>

      {recent.length > 0 ? (
        <div className="space-y-1.5">
          {recent.map((p, i) => (
            <div key={`${p.timestamp}-${i}`} className="flex items-start gap-2">
              <Clock className="h-3.5 w-3.5 flex-shrink-0 text-text-tertiary mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-text-secondary">{formatTimeAgo(p.timestamp)}</div>
                <div className="text-[9px] text-text-tertiary truncate">
                  {p.category.replace(/_/g, ' ')}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded bg-surface-elevated/50 p-2 text-center text-[10px] text-text-tertiary">
          No recent activity
        </div>
      )}
    </div>
  );
}
